/**
 * web-auth SDK 인증을 위한 모듈
 */
import cookie from 'js-cookie'
import { Fetch, generateUUID, getModuleApi } from '@inface-sdk/common'
import InfaceAuthError from './inface-auth-error.class'
import InfaceUser from './user/inface-user.class'
import InfaceClient from './client/inface-client.class'
import GidSetting from './gid/gid-setting.class'
import ENV_VARIABLE from '../constants/env-variable.enum'
import GLOBAL_VARIABLE from "../constants/global-variable.enum"
import PlatformSSO from './platforms/platform-sso.class'
import PlatformKRPC from './platforms/platform-krpc.class'
import PlatformJPPC from './platforms/platform-jppc.class'
import PlatformARENA from './platforms/platform-arena.class'
import PlatformTOY from './platforms/platform-toy.class'
import WebTokenUtil from './utils/webtoken-util.class'
import i18n from 'i18n-js'
import locale2 from 'locale2'
import InfaceLocales from '../i18n'
import { version } from '../../package.json'
import { INFACE_EVENT_TYPE, InfaceService, SDK_STATUS, ToyTermsService, GuestService } from '../services'
import { isAbortOrNetworkError, safeParseJson, sleep } from './utils/common-util'

console.info(`@inface-sdk/web-auth v%s`, `${version}`)

// for IE
if ( !window.location.origin ) {
  window.location.origin  = window.location.protocol + "//" + window.location.hostname + ( window.location.port ? ':' + window.location.port : '' )
}

function getParameterByName(name, search = location.search) {
  const searchParams = new URLSearchParams(search)
  return searchParams.get(name)
}

function getMetaByName(metaName) {
  const el = document.querySelector(`meta[name="${metaName}"]`)

  if (!el) return
  return el.content
}

function getEnvValue() {
  const env = getMetaByName('inface-env') || 'live'
  const key = env.substring(0, 2)
  return ENV_VARIABLE[key]
}

/**
 * 외부 파일 dynamic import
 * @param {String} url
 * @returns {Promise}
 */
function importExternal(url) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = url
    script.async = true
    script.onload = () => resolve(window['external_global_component'])
    script.onerror = reject

    document.body.appendChild(script)
  })
}


// 환경변수
let ENV = getEnvValue()

// API URL
let IAS_API_URL = ENV.IAS_API_URL
let IAS_V2_API_URL = ENV.IAS_V2_API_URL
let IMS_API_URL = ENV.IMS_API_URL

// 지역 강제 선택
const IF_PLATFORM = getMetaByName('inface-platform')
const IF_GID = getMetaByName('inface-gid')

const MODULE_NAME = 'web-auth'

export class InfaceAuth {
  constructor({ webAuth, env, gid, platform, locale, __useNppMonitor } = {}) {

    InfaceService.instance.setModuleName(MODULE_NAME)
    InfaceService.instance.addEventListener(INFACE_EVENT_TYPE.TOY_WEB_SDK_MODULE, this.onInfaceModuleEventListener.bind(this))

    this._webAuth = webAuth
    this._env = env
    this._gid = gid
    this._webToken = cookie && cookie.get(GLOBAL_VARIABLE.WEB_TOKEN_KEY)
    this._platform = null
    this._hasValidWebToken = false
    this.updateIntervalMs = 1000 * 20 // 로그인 시점에서 세션 업데이트 요청 20초 이내에 진행
    this._objInsignSessionTimeout = null
    this._objLocalSessionTimeout = null
    this._objReverseTimeout = null
    this._reverseTimeoutMs = 2000 // 2초
    this._isExpiredReverseTimeout = false
    this._country = null
    this._locale = null

    this._user = new InfaceUser({})
    this._gidSetting = new GidSetting({ gid })
    this._client = new InfaceClient({ domain: this.webAuth })

    this._guest = new GuestService({ gid, user: this._user })

    this._toyTermsService = new ToyTermsService({ gid, locale, webToken: this.webToken, boltApiUrl: ENV.INFACE_BOLT_API_URL })
    this.__useNppMonitor = __useNppMonitor // NPP 모니터링 사용 여부

    if (env) {
      this.setEnv(env)
    }

    this.locale = locale
    this.setIfPlatform(platform)
    this.setIfGid(gid)
  }

  get webAuth() {
    return this._webAuth || getMetaByName('inface-web-auth') || ''
  }

  set webAuth(value) {
    this._webAuth = value
  }

  get env() {
    return this._env || getMetaByName('inface-env') || 'live'
  }

  set env(value) {
    this._env = value
  }

  get targetPlatform() {
    const targetPlatform = this._platform instanceof PlatformSSO ? this._platform.ssoPlatform : this._platform
    return targetPlatform || ''
  }

  get platform() {
    return this._platform || ''
  }

  set platform(value) {
    this._platform = value
  }

  get gid() {
    return this._gid || getMetaByName('inface-gid')
  }

  set gid(value) {
    this._gid = value
  }

  get webToken() {
    return this._webToken || (cookie && cookie.get(GLOBAL_VARIABLE.WEB_TOKEN_KEY))
  }

  set webToken(value) {
    this._webToken = value
  }

  get gidSetting() {
    return this._gidSetting
  }

  set gidSetting(value) {
    this._gidSetting = new GidSetting(value)
  }

  get country() {
    return this._country
  }

  set country(value) {
    this._country = value
  }

  get client() {
    return this._client
  }

  set client(value) {
    this._client = value
  }

  get locale() {
    return this._locale || getMetaByName('inface-locale') || locale2 || 'en-US'
  }

  set locale(value) {
    this._locale = value
    this._setLocale(value)
  }

  get user() {
    return this._user
  }

  get guest() {
    return this._guest
  }

  get toyTermsService() {
    return this._toyTermsService
  }

  setEnv(env) {
    this._env = env
    const key = env.substring(0, 2)
    ENV = ENV_VARIABLE[key]
    this.setApiUrl()
  }

  setApiUrl() {
    const HOSTNAME = location.hostname
    const isSigninPage = HOSTNAME.includes('signin.nexon.com') // singin.nexon.com 에서 호출 시 preflight 제거

    IAS_API_URL = isSigninPage ? ENV.INFACE_IAS_API_URL : ENV.IAS_API_URL
    IAS_V2_API_URL = isSigninPage ? ENV.INFACE_IAS_V2_API_URL : ENV.IAS_V2_API_URL
    IMS_API_URL = isSigninPage ? ENV.INFACE_IMS_API_URL : ENV.IMS_API_URL
  }

  _setLocale(locale = this.locale) {
    // 다국어 적용
    const defaultLanguage = 'en'
    const language = locale?.substring(0, 2) || defaultLanguage

    i18n.defaultLocale = locale
    i18n.locale = !!InfaceLocales[language] ? language : defaultLanguage
    i18n.translations = InfaceLocales

    window.$t = i18n.t

    this.client.setIfCookie(GLOBAL_VARIABLE.LOCALE_KEY, locale)
  }

  
  refreshOtherAuthTab() {
    if (window.inface.itc && window.inface.auth && window.inface.auth._user && window.inface.auth._user._profile && window.inface.auth._user._profile.uid) {
      window.inface.itc.dispatchEvalCommand(`if (window.inface.auth && window.inface.auth._user._profile.uid !== "${window.inface.auth._user._profile.uid}") { console.error("expired insign user session."); location.reload() }`)
    }
    // TOY Web SDK v3 적용
    // if (window.inface.itc && window.inface.auth && window.inface.auth._user && window.inface.auth._user._profile && window.inface.auth._user._profile.uid) {
    //   window.inface.itc.dispatchEvalCommand(`
    //     if (document.querySelector('meta[type="inface"][module="game-auth"')) {
    //       if (!window.inface.auth._user._profile.uid)
    //       return // Play Now 상태일 때 타 tab 에서 넥슨 로그인 했다고 리로드 하지 않음 (넥슨 연동 안해서 wt 없음)
    //     }

    //     if (window.inface.auth && window.inface.auth._user && window.inface.auth._user._profile && window.inface.auth._user._profile.uid !== "${window.inface.auth._user._profile.uid}") {
    //       console.error("expired insign user session.");
    //       location.reload();
    //     }`)
    // }
  }

  injectModule(moduleName) {
    return Object.keys(getModuleApi(moduleName)).length ? getModuleApi(moduleName) : null
  }

  _signedActiveModules() {
    const webPushModule = this.injectModule('web-push')
    // 반드시 GID와 WEB TOKEN 값이 준비되어 있어야 PUSH 서비스를 이용할 수 있습니다
    if (webPushModule && !!this.gid && this.webToken) {
      const pushOption = {
        gid: this.gid,
        cid: this.getCID(),
        webToken: this.webToken,
        env: this.env
      }
      setTimeout(() => {
        webPushModule.startWebPush(pushOption)
        this.refreshOtherAuthTab()
      }, 1000) // 탭 동기화 시간을 확보하기 위한 1초, 1초 이내에 동기화가 완료됨
    } else if (this.webToken) { // 다른 탭에서 다른 계정으로 로그인 시 강제 리로드
      setTimeout(() => {
        this.refreshOtherAuthTab()
      }, 1000) // 탭 동기화 시간을 확보하기 위한 1초, 1초 이내에 동기화가 완료됨
    }
  }


  _syncAccountInfoForOtherTab() {
    if (this.webToken) { // 다른 탭에서 다른 계정으로 로그인 시 강제 리로드
      setTimeout(() => {
        this.refreshOtherAuthTab()
      }, 1000) // 탭 동기화 시간을 확보하기 위한 1초, 1초 이내에 동기화가 완료됨
    }
  }

  setIfPlatform(platform = IF_PLATFORM) {
    const HOSTNAME = location.hostname
    const isSigninPage = HOSTNAME.includes('signin.nexon.com')

    this.platform = platform
    if (platform) {
      this.client.setCookie(GLOBAL_VARIABLE.PLATFORM_KEY, platform)
    } else if (!isSigninPage) {
      this.client.removeCookie(GLOBAL_VARIABLE.PLATFORM_KEY)
    }
  }
  
  setIfGid(gid = IF_GID) {
    const HOSTNAME = location.hostname
    const isSigninPage = HOSTNAME.includes('signin.nexon.com')

    if (gid) {
      this.client.setCookie(GLOBAL_VARIABLE.GID_KEY, gid)
    } else if (!isSigninPage) {
      this.client.removeCookie(GLOBAL_VARIABLE.GID_KEY)
    }
  }

  handleBeforeunload() {
    this._objInsignSessionTimeout && clearTimeout(this._objInsignSessionTimeout)
    this._objLocalSessionTimeout && clearTimeout(this._objLocalSessionTimeout)
  }

  /**
   * web-auth 초기화 함수
   *
   * @memberof {@link InfaceAuth}
   * @param {Object} args
   * @param {string} args.webAuth - INSIGN 연동처에 대한 도메인 정보
   * @param {string} args.env - 환경값 `(local, dev, test, pre, live)`
   * @param {string} args.gid - GID (TOY service ID)
   * @param {string} args.platform - 플랫폼 값 `(krpc, jppc, arena_west, arena_th, arena_tw, arena_sea)`
   * @param {requestCallback} args.callbackOk - 성공 콜백
   * @param {requestCallback} args.callbackFail - 실패 콜백
   * @param {string} args.krpc.memberAccessCode - KRPC 로그인 진입처 구분 용도의 코드 값
   * @param {string} args.jppc.gm - JPPC 로그인 진입처 구분 용도의 코드 값
   * @param {boolean} args.checkAdditionalTerms - 추가약관조회 여부에 대한 플래그 값
   * @param {boolean} args.useGuestSession - 게스트 세션 사용 여부에 대한 플래그 값
   */
  async init({ 
    webAuth, env, locale, 
    gid = IF_GID, platform = IF_PLATFORM, 
    callbackOk, callbackFail, 
    krpc = {}, jppc = {}, 
    checkAdditionalTerms,
    useGuestSession,
    __useNppMonitor,
  } = {}) {
    try {
      window.addEventListener('beforeunload', this.handleBeforeunload)
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === "visible") {
          setTimeout(() => {
            this.verifyWebToken() // 포커스 돌아왔을 때 토큰 검증
          }, 1000)
        }
      })
      window.addEventListener('online', () => this.verifyWebToken()) // 네트워크 복구 시 토큰 검증

      this.__useNppMonitor = __useNppMonitor

      if (webAuth) {
        this.webAuth = webAuth
        this.client.domain = webAuth
      }

      if (env) {
        this.setEnv(env)
      }

      this._guest = new GuestService({ 
        gid,
        ticket: getParameterByName('web_ticket') || getParameterByName('ticket'),
        iasApiUrl: ENV.IAS_API_URL,
        useGuestSession,
        user: this.user,
        tokenRefreshInterval: ENV.WEB_TOKEN_PERIOD_MS
      })
      await this._guest.init()
      
      if (gid) {
        this._gid = gid
        this.gidSetting = await this.getGidSetting({ gid })
        
        // 우선순위: 입력받은 krpc.memberAccessCode > gidSetting 설정
        if (!krpc.memberAccessCode && this.gidSetting.krpcMemberAccessCode) {
          // 콘솔에서 가져온 krpc_member_access_code 추가
          krpc.memberAccessCode = this.gidSetting.krpcMemberAccessCode
        }
        if (!jppc.gm && this.gidSetting.jppcGm) {
          jppc.gm = this.gidSetting.jppcGm
        }
      }

      if (locale) {
        this.locale = locale
      }

      this.setIfPlatform(platform)
      this.setIfGid(gid)

      // this._showLoading()

      // 웹티켓 확인
      await this.processSsoByWebTicketForCors()

      // 플랫폼 조회
      const platformResult = await this.getPlatform('selector', { krpc, jppc })
      if (platformResult.code > 4000) {
        throw new InfaceAuthError({ code: platformResult.code, response: platformResult.response, data: platformResult.errorData })
      }
      this._platform = platformResult.platform


      // 토큰 검증
      const verifyResult = await this.verifyWebToken()
      if (verifyResult.code === 200) { // 로그인
        this._hasValidWebToken = true
      } else if (verifyResult.code > 4000) {
        throw new InfaceAuthError({ code: verifyResult.code, response: verifyResult.response, data: verifyResult.errorData })
      } else { // 비로그인
        // 역방향 세션 연동
        const reverseResult = await this.reverseFlow()
        if (reverseResult.code === 200) { // 로그인
          this._hasValidWebToken = true
        } else if (reverseResult.code > 4000) {
          throw new InfaceAuthError({ code: reverseResult.code, response: reverseResult.response, data: reverseResult.errorData })
        }
      }


      if (this._hasValidWebToken) {
        this._signedActiveModules()
        // this._syncAccountInfoForOtherTab()
        this._updateLocalSession()
        this._updateInsignSession()

        this._toyTermsService = new ToyTermsService({ 
          gid: this.gid, 
          locale: this.locale, 
          webToken: this.webToken, 
          checkAdditionalTerms, 
          boltApiUrl: ENV.INFACE_BOLT_API_URL 
        })

        const { success, data } = await this._toyTermsService.processCheckTerms()
        if (!success) { // UI 가 필요한 처리는 gw 로 이동
          this.gotoLogin()
          return
        }

        const { termsAgree, guid, ticket } = data || {}
        if (termsAgree?.length) { // 추가 약관 존재
          this.gotoConsentTerms({ guid, ticket })
          return
        }
      }

      if (typeof callbackOk === 'function') {
        callbackOk()
      }
    } catch (e) {
      const err = e instanceof InfaceAuthError ? e : new InfaceAuthError({ code: 113002, data: e }) // 113002: fail to init
      console.error('Code: ', err.code, err)

      if (typeof callbackFail === 'function') {
        callbackFail(err)
        return
      }

      throw err
    } finally {
      this._hideLoading()
      InfaceService.instance.sendStatusChangeEvent({ status: SDK_STATUS.INITIALIZED, additionalData: { webToken: this.webToken } })

      this._sendWebTokenSdkEvent()
    }
  }

  _sendWebTokenSdkEvent() {
    // webToken 변경 전파 필요한 곳에서 사용
    const webToken = this.webToken
    const eventData = { type: 'webToken', data: { status: webToken ? 'signin' : 'signout', webToken } }
    InfaceService.instance.sendSdkEvent({ eventData })
  }

  async _updateLocalSession() {
    const targetPlatform = this._platform instanceof PlatformSSO ? this._platform.ssoPlatform : this._platform

    // 로컬 플랫폼 세션 갱신(KRPC, JPPC)
    const url = targetPlatform.authLibUrl
    if (url && targetPlatform.isValidSession()) {
      await importExternal(url)
      this._objLocalSessionTimeout = setTimeout(() => this.doUpdateSession(), this.updateIntervalMs) // interval 뒤에 갱신
    }
  }

  _updateInsignSession() {
    const lifecycle = ENV.WEB_TOKEN_PERIOD_MS
    const interval = lifecycle / 2 - 60000

    if (this._objInsignSessionTimeout) {
      clearTimeout(this._objInsignSessionTimeout)
    }
    
    this._objInsignSessionTimeout = setTimeout(async () => {
      const verifyResult = await this.verifyWebToken()
      if (verifyResult.code === 200) {
        this._updateInsignSession()
      } else {
        this.reverseFlow()
      }
    }, interval)
  }

  /**
   * 로컬 플랫폼을 조회합니다.
   * API 정책
   *  - ?ifplatform= (쿼리는 있지만 빈 값일 때) -> country 값 기반으로 제공 (쿠키 바라보지 않음)
   *  - ? (쿼리가 없을 때) -> 쿠키 바라봄
   * 
   * @param {string} options.krpc.memberAccessCode - KRPC 로그인 진입처 구분 용도의 코드 값
   * @param {string} options.jppc.gm - JPPC 로그인 진입처 구분 용도의 코드 값
   */
  async getPlatform(selector = 'selector', options = {}) {
    try {
      // CORS 회피를 위해 signin.nexon.com 에서는 다른 API 호출 (기능 동일)
      const isSignin = selector === 'selector-for-signin'
      const apiUrl = isSignin ? IMS_API_URL : IAS_API_URL // signin.nexon.com 에서는 간편가입을 위해 ims 호출
      const ifplatform = this.client.getCookie(GLOBAL_VARIABLE.PLATFORM_KEY)
      const searchParams = isSignin ? { ifplatform: encodeURIComponent(this.platform) } : ifplatform ? { ifplatform: encodeURIComponent(ifplatform) } : {} // Example) KR:krpc2&KR:krpc&JP:arena_jp&arena_west
      const platformQuery = new URLSearchParams(searchParams).toString()
      
      const url = `${apiUrl}/platform/${selector}?${platformQuery}`
      const response = await Fetch(url, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-store',
        },
        mode: 'cors',
        credentials: 'include'
      })

      if (!response.ok) {
        throw response
      }

      let data = await response.json()
      const platform = data.platform
      this.country = data.country

      let targetPlatform = null
      if (['krpc'].includes(platform)) {
        const npp = data.npp || this.client.getCookie('NPP') // IE CORS 요청시 쿠키 제거 이슈
        const authLibUrl = ENV.KRPC_AUTH_LIB_URL
        targetPlatform = new PlatformKRPC({ npp, authLibUrl, memberAccessCode: options?.krpc?.memberAccessCode, __useNppMonitor: this.__useNppMonitor })
      } else if (['arena_west', 'arena_th', 'arena_tw', 'arena_sea', 'arena_jp', 'arena'].includes(platform)) {
        const region = platform.split('_')[1] || WebTokenUtil.getPlatformByWebToken(this.webToken)?.split('_')[1] // arena 일 때 로그아웃/계정관리를 위해 지역 값 필요
        const arenaToken = data.arena_token || this.client.getCookie('g_AToken') // 지역 강제 선택 (http only 해제 시 사용 가능)
        targetPlatform = new PlatformARENA({ region, arenaToken })
      } else if (['jppc'].includes(platform)) {
        const npp = data.npp || this.client.getCookie('NPP') // IE CORS 요청시 쿠키 제거 이슈
        targetPlatform = new PlatformJPPC({ npp, gm: options?.jppc?.gm })
      }

      if (!targetPlatform) {
        return { code: 113001, errorData: { platform } }
      }

      this.client.setCookie(GLOBAL_VARIABLE.PLATFORM_SELECTOR_KEY, platform)

      if (this.hasSignedSSO()) {
        const ssoSession = this.client.getCookie(GLOBAL_VARIABLE.WEB_SSO_SESSION_KEY)
        return { code: 200, platform: new PlatformSSO({ platform: targetPlatform, session: ssoSession })}
      }

      return { code: 200, platform: targetPlatform, data }
    } catch(e) {
      if (typeof e.json === "function") {
        const errorData = await e.json()

        if (e.status === 403 && (errorData?.error?.code === 18 || errorData?.error?.name === 'forbidden')) { // RU 등 지원하지 않는 지역 또는 GW 지역 거부 필터
          return { code: 113001, response: e, errorData } // 미 서비스 지역 
        }

        return { code: 113101, response: e, errorData } // 플랫폼 조회 실패
      } else {
        return { code: 113101, response: e, errorData: e } // 플랫폼 조회 실패
      }
    }
  }

  /**
   * 로컬 세션 유효성 확인 (아래의 경우 로컬 세션 검증 pass)
   * - nexon.com 외 다른 도메인일 경우 (로컬 세션을 쿠키에서 읽어올 수 없음)
   * - arena, jppc 플랫폼의 간편가입 직후 (krpc 는 nest 통해 npp 생성)
   */
  isInvalidLocalSession() {
    if (this.client.useCors) {
      return false
    }

    const isSimpleSignup = this.webToken?.includes('@simplesignup')
    if (this._platform !== 'krpc' && isSimpleSignup) {
      return false
    }

    return !this._platform.isValidSession()
  }

  /**
   * 웹토큰의 플랫폼 타입 유효성 확인 (아래의 경우 검증 pass)
   * - sso 인증일 경우
   * - 설정된 리전락 플랫폼과 동일한 경우
   */
  isInvalidWebTokenPlatformType(data) {
    if (this._platform instanceof PlatformSSO) {
      return false
    }

    const isDifferentPlatformType = this.platform.type && data.platform_type?.toLowerCase() !== this.platform.type
    const isDifferentArenaRegion = this.platform.type === 'arena' && this.platform.regionType && this.platform.regionType !== data.arena_region_type

    return isDifferentPlatformType || isDifferentArenaRegion
  }

  /**
   * NOTE. [PWFE-1591] 240106 NPP, wt 유저 일치 여부 확인 기능 추가 (IAS V2)
   * @returns {Boolean}
   */
  async verifyWebToken(opts = {}) {
    const { signal, maxRetry = 4 } = opts

    try {
      if (!this.webToken) {
        return { code: 401 }
      }

      if (!this._platform) {
        return { code: 113001 }
      }

      console.debug('verifyWebToken > isInvalidLocalSession', this.isInvalidLocalSession())
      if (this.isInvalidLocalSession()) {
        console.warn('로컬 세션이 유효하지 않아 웹토큰이 만료 됩니다.')
        return await this.expireWebToken()
      }

      const body = (typeof this._platform.getVerifyWebTokenBody === 'function')
        ? this._platform.getVerifyWebTokenBody() 
        : {}
        
      const attempt = async () => {
        try {
          const response = await Fetch(`${IAS_V2_API_URL}/verify/web-token`, {
            method: 'POST',
            mode: 'cors',
            credentials: 'include',
            headers: {
              "x-ias-web-token": this.webToken,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ ...body, domain: location.origin }),
            signal,
          })

          if (!response.ok) {
            const errorData = await safeParseJson(response);

            // 서버가 명확히 "세션 무효"를 알리는 케이스
            if (response.status === 401) {
              const isLocalSessionExpiredFromInfaceGw = !!(errorData?.error?.code === undefined) // Inface GW 에서 로컬세션 만료로 인해 401 응답을 준 경우 (Authorization 헤더로 검증)

              if (isLocalSessionExpiredFromInfaceGw) {
                console.warn('지역세션이 만료되었으므로 웹토큰을 만료 처리합니다.')
                return await this.expireWebToken()
              }
              
              if (this._platform instanceof PlatformSSO) {
                this._platform.clear()
              }
              this.clearInsignSession()
              return { code: 401, response, errorData } // 비로그인 상태
            }

            // 서버 문제(5xx): 세션은 유지하고 실패 코드만 반환
            if (response.status >= 500) {
              return { code: 113103, response, errorData }
            }
            
            return { code: 400, response, errorData }
          }

          // 2xx
          const data = await response.json()

          // 플랫폼 타입 불일치 → 웹토큰 만료 처리
          if (this.isInvalidWebTokenPlatformType(data)) {
            const platformTypeData = {
              web_token_platform_type: data.platform_type.toLowerCase(),
              current_platform_type: this.platform.type,
              arena_region_type: data.arena_region_type,
              platform_region_type: this.platform.regionType,
            }

            console.warn(`로컬 세션의 플랫폼 타입과 웹토큰의 플랫폼 타입이 일치하지 않아 웹토큰이 만료 됩니다.\n${JSON.stringify(platformTypeData)}`)
            const expireResult = await this.expireWebToken({ doClearSession: false })
            this.clearInsignSession()
            return expireResult
          }

          this._user.setProfile(data)
          return { code: 200 }
        } catch (e) {
          // 네트워크 단절/사용자 취소/백그라운드 전환 등
          if (isAbortOrNetworkError(e)) {
            // 절대 여기서 세션을 지우지 않음
            return { code: 0, transient: true, response: e, errorData: e }
          }
          console.error('[verifyWebToken] unexpected error', e)
          return { code: 113103, response: e, errorData: e }
        }
      }

      // status:0(일시적)일 때만 짧게 재시도 (지수 백오프)
      let attemptCount = 0
      let result = await attempt()
      while (result.code === 0 && result.transient && attemptCount < maxRetry) {
        await sleep(Math.min(300 * 2 ** attemptCount, 2000))
        attemptCount += 1
        result = await attempt()
      }

      return result
    } catch(e) {
      console.error('[verifyWebToken] outer error', e);
      return { code: 113103, response: e, errorData: e };
    }
  }

  clearInsignSession() {
    // 웹토큰 만료 실패시(정상 토큰 아닐시) 무한히 실패 케이스로 남을 경우가 있으므로 쿠키 먼저 삭제
    this.client.removeCookie(GLOBAL_VARIABLE.WEB_TOKEN_KEY)
    this.client.removeCookie(GLOBAL_VARIABLE.WEB_SSO_SESSION_KEY)
    this._webToken = null
  }
  
  clearSession() {
    this.clearInsignSession()

    // KRPC 만료시, NPP, IL 쿠키 삭제 해주지 않을 경우 .nexon.com에서 예외발생
    this._platform && this._platform.clear()
  }

  /**
   * web_token 을 만료시키고 cookie 에서 제거합니다.
   */
  async expireWebToken({ doClearSession = true } = {}) {
    try {
      if (this.webToken) {
        const response = await Fetch(`${IAS_API_URL}/expire/web-token`, {
          method: 'POST',
          mode: 'cors',
          headers: {
            "x-ias-web-token": this.webToken,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ 
            do_clear_session: doClearSession,
            clear_session_platform_type: this.targetPlatform.type.toUpperCase()
          })
        })

        if (!response.ok) {
          throw response
        }
      }

      if (doClearSession) {
        this.clearSession()
      }
      
      return { code: 401 } // 비로그인 상태

    } catch(e) {
      const errorData = typeof e.json === "function" ? await e.json() : e
      this.clearSession()
      return { code: 113102, response: e, errorData } // 만료 실패
    }
  }

  isValidWebToken(wt) {
    return !!wt && typeof wt === 'string' && wt.indexOf('ias:wt') > -1 && wt.split(':').length > 3
  }

  isValidWebTicket(wti) {
    return !!wti && typeof wti === 'string' && wti.indexOf('ias:wti') > -1 && wti.split(':').length > 3
  }

  /**
   * @deprecated
   * 유저 프로파일 정보를 조회합니다.
   * @returns {Object}
   */
  async getProfile(callback) {
    try {
      console.debug(`inface.auth.getProfile() 함수는 deprecated 되었습니다. 닉네임 등의 유저 상세정보 조회를 위해서는 "[GET] https://private.api.nexon.com/ims/v1/profile/uid/{uid}" API 를 이용해주세요.\nuid는 로그인 된 사용자에 한해 await inface.auth.getUserProfile() 로 가져올 수 있습니다.`)

      if (!this.isValidWebToken(this.webToken)) {
        const err = new InfaceAuthError({ code: 113010 })
        throw err
      }

      const response = await Fetch(`${IAS_API_URL}/user/profile`, {
        method: 'GET',
        mode: 'cors',
        headers: {
          "x-ias-web-token": this.webToken
        }
      })

      if (!response.ok) {
        throw response
      }

      const data = await response.json()
      this._user.setProfile(data)

      const profile = this._user.getProfile()

      if (typeof callback === 'function') {
        callback(profile)
        return
      }

      return profile
    } catch(e) {
      if (e instanceof InfaceAuthError) {
        throw e
      }

      const errorData = typeof e.json === "function" ? await e.json() : e
      const err = new InfaceAuthError({ code: 113107, response: e, data: errorData })
      throw err
    }
  }

  /**
   * @callback requestCallback
   * @param {import('./inface-auth-error.class').default} error
   * @param {import('./user/inface-user-profile.class').default} data
   */
  /**
   * @typedef {Object} ProfileResult
   * @property {import('./inface-auth-error.class').default | object} error
   * @property {import('./user/inface-user-profile.class').default | object} data
   */
  /**
   * 유저 프로파일 정보를 조회합니다.
   * @param {requestCallback} [callback] - callback 을 전달하면 값을 반환하지 않음
   * @returns {ProfileResult}
   */
  async getUserProfile(callback) {
    try {
      // 게스트 세션 존재 시 게스트 세션 정보 반환
      const verifyResultForGuest = await this.guest.processVerifyGuestSession()
      if (verifyResultForGuest.success) {
        this._user.setProfile(verifyResultForGuest.data)
        const profile = this.user.getProfile()
        if (typeof callback === 'function') {
          callback(null, profile)
          return
        }
        return { error: null, data: profile }
      }

      const verifyResult = await this.verifyWebToken()
      if (verifyResult.code !== 200) { // 비로그인
        const error = new InfaceAuthError({ code: 113010 })
        if (typeof callback === 'function') {
          callback(error, {})
          return
        }
        
        return { error, data: {} }
      }
      
      const profile = this.user.getProfile()

      if (typeof callback === 'function') {
        callback(null, profile)
        return
      }

      return { error: null, data: profile }
    } catch(e) {
      if (e instanceof InfaceAuthError) {
        if (typeof callback === 'function') {
          callback(e)
          return
        }
        return { error: e }
      }

      const errorData = typeof e.json === "function" ? await e.json() : e
      const error = new InfaceAuthError({ code: 113107, data: errorData })
      if (typeof callback === 'function') {
        callback(error)
        return
      }
      return { error }
    }
  }

  /**
   * web_token 을 발급받아 값을 쿠키에 저장합니다.
   * @param {object} platform // selector 에 해당하는 platform 인스턴스
   * @return {object} { code: number, data: object, response: Response, errorData: object }
   */
  async _getWebToken(platform) {
    try {
      if (!platform) {
        return { code: 113001 }
      }

      if (this.hasSignedSSO()) {
        this.webToken = this.client.getCookie(GLOBAL_VARIABLE.WEB_TOKEN_KEY)
        return { code: 200, data: { web_token: this.webToken } }
      }

      const { useCorsReferrer, referrerDomain } = this.client
      const body = platform.getWebTokenBody()
      let corsPath = ''

      if (useCorsReferrer) {
        body.cross_domain = referrerDomain
        corsPath = 'cors/'
      }
      console.log('body', body)

      const url = `${IAS_API_URL}/login/${corsPath}${platform.type}`

      const response = await Fetch(url, {
        method: 'POST',
        mode: 'cors',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...body, domain: location.origin })
      })

      if (!response.ok) {
        throw response
      }

      const data = await response.json()
      this._user.setProfile(data)

      if (!this.isValidWebToken(data.web_token) || (useCorsReferrer && !data.web_ticket)) {
        throw data
      }

      this.webToken = data.web_token
      this.client.setCookie(GLOBAL_VARIABLE.WEB_TOKEN_KEY, data.web_token)

      return { code: 200, data }

    } catch(e) {
      const errorData = typeof e.json === "function" ? await e.json() : e

      if (e.status === 401) { // 지역세션이 만료되었을 경우
        this.clearSession()
        return { code: 401, response: e, errorData }
      }

      return { code: 400, response: e, errorData }
    }
  }

  /**
   * 웹티켓을 통해 웹토큰과 토큰의 플랫폼 정보를 전달 받습니다.
   *
   * @param {string} webTicket
   * @return {object} { code: number, data: object, response: Response, errorData: object }
   * @memberof InfaceAuth
   */
  async _getWebTokenByWebTicketForCors(webTicket) {
    try {
      if (!webTicket) {
        return { code: 113015 }
      }

      const response = await Fetch(`${IAS_API_URL}/login/cors/by-web-ticket`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ web_ticket: webTicket })
      })

      if (!response.ok) {
        throw response
      }

      const data = await response.json()
      this._user.setProfile(data)

      if (!this.isValidWebToken(data.web_token)) {
        throw data
      }

      this.webToken = data.web_token

      this.client.setCookie(GLOBAL_VARIABLE.WEB_TOKEN_KEY, data.web_token)

      return { code: 200, data }

    } catch (e) {
      const errorData = typeof e.json === "function" ? await e.json() : e
      return { code: 113111, response: e, errorData }
    }
  }

  /**
   * URL 노출되는 웹뷰 인증 시 일회용 로그인을 위해 발급된 웹티켓을 웹토큰으로 변환
   *
   * @param {string} webTicket // game_token 을 통해서 발급된 웹티켓
   * @param {string} gid // 웹티켓 발급 시 사용된 gid 와 일치
   * @param {string} domain // optional
   * @param {boolean} useCookie // optional - 쿠키에 웹토큰 저장 여부 (default: true)
   * @return {object} { code: number, data: object, response: Response, errorData: object }
   * @memberof InfaceAuth
   */
  async _getWebTokenByWebTicketForSsoByTicket({ webTicket, gid, domain, useCookie = true }) {
    try {
      if (!gid || !this.gid) {
        return { code: 113011 }
      }

      if (!webTicket) {
        return { code: 113015 }
      }

      const response = await Fetch(`${IAS_API_URL}/issue/web-token/by-web-ticket`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ 
          web_ticket: webTicket, 
          gid: gid || this.gid,
          domain 
        })
      })

      if (!response.ok) {
        throw response
      }

      const data = await response.json()

      if (!this.isValidWebToken(data.web_token)) {
        throw data
      }

      this.webToken = data.web_token

      if (useCookie) {
        this.client.setCookie(GLOBAL_VARIABLE.WEB_TOKEN_KEY, data.web_token)
      }

      return { code: 200, data }

    } catch (e) {
      const errorData = typeof e.json === "function" ? await e.json() : e
      const errorName = errorData?.error?.name

      if (e.status === 401 && errorName === 'IAS_UNAUTHORIZED_ERROR') { // 인증 오류(웹티켓 오류)
        return { code: 113110, response: e, errorData }
      } else if (e.status === 400 && errorName === 'IAS_VALIDATION_FAILED') { // IP 또는 gid 불일치
        return { code: 113113, response: e, errorData }
      }

      return { code: 113112, response: e, errorData }
    }
  }

  /**
   * 게임토큰에서 발급된 웹티켓을 통해 웹토큰을 발급받습니다.
   * 
   * @param {string} webTicket // game_token 을 통해서 발급된 웹티켓
   * @param {string} gid // 웹티켓 발급 시 사용된 gid 와 일치
   * @param {string} domain // optional
   * @param {boolean} useCookie // optional - 쿠키에 웹토큰 저장 여부 (default: true)
   * @returns {object} { web_token: string }
   */
  async getWebTokenByWebTicketFromGameToken({ webTicket, gid, domain, useCookie = true }) {
    try {
      const { code, data, response, errorData } = await this._getWebTokenByWebTicketForSsoByTicket({ webTicket, gid, domain, useCookie })

      if (code !== 200) {
        throw new InfaceAuthError({ code, response, data: errorData }) // 웹토큰 발급 실패 (by 웹티켓)
      }

      // 세션 유지
      if (useCookie) {
        this.client.setCookie(GLOBAL_VARIABLE.WEB_SSO_SESSION_KEY, webTicket)
      }

      return { web_token: data?.web_token }
    } catch (e) {
      throw e
    }
  }

  /**
   * 게임토큰을 통해 웹토큰과 토큰의 플랫폼 정보를 전달 받습니다.
   *
   * @param {string} gameToken
   * @return {object} { web_token: string, local_session_type: string}
   * @memberof InfaceAuth
   */
  async _getWebTokenByGameToken(gameToken) {
    try {
      if (!gameToken) {
        return { code: 404 }
      }

      const response = await Fetch(`${IAS_API_URL}/issue/web-token/by-game-token`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "x-ias-game-token": gameToken
        }
      })

      if (!response.ok) {
        throw response
      }

      const data = await response.json()

      if (!this.isValidWebToken(data.web_token)) {
        throw data
      }

      this.webToken = data.web_token
      this.client.setCookie(GLOBAL_VARIABLE.WEB_TOKEN_KEY, data.web_token)

      return { code: 200, data }

    } catch (e) {
      const errorData = typeof e.json === "function" ? await e.json() : e
      return { code: 400, response: e, errorData }
    }
  }

  async getWebTokenByGameToken({ gameToken, npToken }) {
    try {
      const webSSOSession = gameToken || npToken

      // game_token to web_token
      if (gameToken) {
        const webTokenInfo = await this._getWebTokenByGameToken(gameToken)

        if (webTokenInfo.code !== 200) {
          throw new InfaceAuthError({ code: 113105, response: webTokenInfo.response, data: webTokenInfo.errorData }) // 웹토큰 발급 실패 (by 게임토큰)
        }
      }

      // np_token to web_token
      if (npToken) {
        const toyPlatform = new PlatformTOY({ npToken })
        const { code, response, errorData } = await this._getWebToken(toyPlatform)

        if (code !== 200) {
          throw new InfaceAuthError({ code: 113106, response, data: errorData }) // 웹토큰 발급 실패 (by np토큰)
        }
      }

      this.client.setCookie(GLOBAL_VARIABLE.WEB_SSO_SESSION_KEY, webSSOSession)

      return { web_token: this.webToken, web_sso_session: webSSOSession }
    } catch (e) {
      throw e
    }
  }

  /**
   * 게임토큰을 통해 게스트 계정을 위한 1회성 로그인용 웹티켓을 발급 받습니다.
   *
   * @param {string} gameToken
   * @param {string} domain // 게스트 웹토큰 발급 시 사용될 도메인 (optional)
   * @return {{ code: number, data?: { web_ticket: string }, response?: Error, errorData?: string }}
   * @memberof InfaceAuth
   */
  async _getWebTicketByGameTokenForGuestLink({ gameToken, domain }) {
    try {
      if (!gameToken) {
        return { code: 404 }
      }

      const response = await Fetch(`${IAS_API_URL}/issue/web-ticket/by-game-token`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "x-ias-game-token": gameToken,
        },
        body: JSON.stringify({
          domain,
        })
      })

      if (!response.ok) {
        throw response
      }

      const data = await response.json()

      if (!this.isValidWebTicket(data.web_ticket)) {
        throw data
      }

      return { code: 200, data }
    } catch (e) {
      const errorData = typeof e.json === "function" ? await e.json() : e
      return { code: 400, response: e, errorData }
    }
  }

  /**
   * 게임토큰을 통해 CORS redirect를 위한 1회성 로그인용 웹티켓을 발급 받습니다.
   *
   * @param {string} gameToken
   * @param {string} gid // 웹티켓 발급 시 사용된 gid 와 일치
   * @param {string} domain // 웹토큰 발급 시 사용될 도메인
   * @return {{ code: number, data?: { web_ticket: string }, response?: Error, errorData?: string }}
   * @memberof InfaceAuth
   */
  async _getWebTicketByGameTokenForCors({ gameToken, npToken, gid, domain }) {
    try {
      if (!(gameToken || npToken) || !gid || !domain) {
        return { code: 404 }
      }

      const body = {
        game_token: gameToken || npToken,
        gid,
        cross_domain: domain,
      }

      const response = await Fetch(`${IAS_API_URL}/login/cors/by-game-token`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...body })
      })

      if (!response.ok) {
        throw response
      }

      const data = await response.json()

      if (!this.isValidWebTicket(data.web_ticket) || !this.isValidWebToken(data.web_token)) {
        throw data
      }

      return { code: 200, data }
    } catch (e) {
      const errorData = typeof e.json === "function" ? await e.json() : e
      return { code: 400, response: e, errorData }
    }
  }

  /**
   * game_token to web_ticket
   * @param {*} param0 
   * @returns 
   */
  async getWebTicketByGameToken({ gameToken, npToken, gid, domain, isCors = false }) {
    try {
      // SSO for CORS
      if (isCors) {
        const webTicketInfo = await this._getWebTicketByGameTokenForCors({ gameToken, npToken, gid, domain })

        if (webTicketInfo.code !== 200) {
          throw new InfaceAuthError({ code: 113114, response: webTicketInfo.response, data: webTicketInfo.errorData }) // SSO CORS 로그인용 웹티켓 발급 실패 (by 게임토큰 or NP토큰)
        }

        return { web_ticket: webTicketInfo.data?.web_ticket }
      }

      // SSO for Guest Link
      if (gameToken) {
        const webTicketInfo = await this._getWebTicketByGameTokenForGuestLink({ gameToken, domain })

        if (webTicketInfo.code !== 200) {
          throw new InfaceAuthError({ code: 113108, response: webTicketInfo.response, data: webTicketInfo.errorData }) // 웹티켓 발급 실패 (by 게임토큰)
        }

        return { web_ticket: webTicketInfo.data?.web_ticket }
      }

      // np_token to web_ticket
      if (npToken) {
        throw new InfaceAuthError({ code: 113109, message: '모바일 세션(npToken)으로 웹티켓 발급이 불가능합니다.', data: { npToken } }) // 웹티켓 발급 실패 (by np토큰)
      }

      return {}
    } catch (e) {
      throw e
    }
  }

  async processSsoByWebTicketForCors() {
    try {
      const isInsignPath = location.pathname.replace(/\/$/g, '') === GLOBAL_VARIABLE.WEB_SSO_INSIGN_PATH // path 뒤에 / 붙는 케이스 존재
      const ticket = getParameterByName('ticket')
      console.log('sso', isInsignPath, ticket)

      if (!isInsignPath) return

      const { code, response, errorData: data } = await this._getWebTokenByWebTicketForCors(ticket)
      if (code !== 200) {
        throw new InfaceAuthError({ code, response, data }) // 웹토큰 발급 실패 (by 웹티켓)
      }

      // /insign 페이지 -> 게임 사이트로 이동
      const redirectUri = getParameterByName('redirect_uri')
      location.href = redirectUri
    } catch(e) {
      throw e
    }
  }

  _showLoading() {
    if (!document || !document.body) return

    if (document.querySelector('#inface-auth-loading')) return

    const loadingEl = document.createElement('div')

    loadingEl.setAttribute('id', 'inface-auth-loading')
    loadingEl.setAttribute('style', 'position:fixed;top:0;left:0;width:100%;height:100vh;background-color:#fff;opacity:1')
    // loadingEl.innerHTML = '<div style="display:flex;justify-content:center;align-items:center;height:100%"><b>loading...</b></div>'

    document.body.appendChild(loadingEl)
  }

  _hideLoading() {
    if (!document || !document.body) return

    const loadingEl = document.querySelector('#inface-auth-loading')
    if (loadingEl) {
      document.body.removeChild(loadingEl)
    }
  }

  /**
   * 역방향 세션 연동 플로우를 진행합니다.
   * @returns {Promise}
   */
  async reverseFlow() {
    try {
      if (!this._platform) {
        return { code: 113001 }
      }

      if (!this._platform.isValidSession()) {
        return { code: 401 } // 비로그인
      }

      // this._showLoading()

      const { code, data, response, errorData } = await this._getWebToken(this._platform)

      if (code === 200) {
        clearTimeout(this._objReverseTimeout)
        return { code: 200, data } // 로그인
      } else if (code === 401) {
        clearTimeout(this._objReverseTimeout)
        return { code, response, errorData } // 비로그인
      }

      // 역방향 재시도 타임아웃 만료 시
      if (this._isExpiredReverseTimeout) {
        throw errorData // 에러 발생
      }

      // 역방향 재시도 타임아웃
      if (!this._objReverseTimeout) {
        this._objReverseTimeout = setTimeout(() => {
          this._isExpiredReverseTimeout = true
        }, this._reverseTimeoutMs)
      }

      // 0.5 초 기다린다
      const sleep = (ms) => new Promise((resolve) => setTimeout(() => {resolve()}, ms))
      await sleep(500)

      // 역방향 오류 시 재시도
      return await this.reverseFlow()

    } catch(e) {
      this._hideLoading()

      const errorData = typeof e.json === "function" ? await e.json() : e
      return { code: 113104, response: e, errorData } // 토큰 발급 실패
    }
  }

  /**
   * 로컬 플랫폼 세션 갱신
   */
   doUpdateSession() {
    try {
      AuthSystem && AuthSystem.UpdateSession( (resultObject) => this._onUpdateResponse(resultObject) )
    } catch(e) {
      const err = new Error($t('FAIL_TO_UPDATE_LOCAL_SESSION'))
      console.error(err)
    }
  }

  milliseconds(x) {
    if (isNaN(x)) {
      x = 60
    }
    return x * 1000
  }

  /**
   * 로컬 플랫폼 세션 갱신 callback
   */
  _onUpdateResponse( resultObject ) {
    if ( resultObject.ErrorCode == undefined ) { // Auth Server 에 Session 이 존재하지 않으므로 로그아웃 처리
      console.log('Auth Server 에 Session 이 존재하지 않으므로 로그아웃 처리')
      this.gotoSignOut()
    } else if( resultObject.ErrorCode == CommonError.NoError ) { // OK
      this._platform.updateSession()
      // 로그아웃 얼럿 한번만 띄우기 위해 setInterval -> setTimeout 으로 변경
      this._objLocalSessionTimeout = setTimeout(() => this.doUpdateSession(), this.milliseconds(resultObject.UpdateInterval)) // ExpireLimit(Second) 이후, UpdateSession
    } else if( resultObject.ErrorCode == AuthSystemError.ServiceShutdown ) { // 20002
      alert( $t('LOGOUT_BY_SERVICE_SHUTDOWN') )
      this.gotoSignOut()
    } else if( resultObject.ErrorCode == AuthSystemError.Disconnected ) { // 20018
      alert( $t('LOGOUT_BY_DISCONNECTED') )
      this.gotoSignOut()
    } else if( resultObject.ErrorCode == AuthSystemError.InvalidUserIP ) { // 20014
      alert( $t('LOGOUT_BY_INVALID_USER_IP') )
      this.gotoSignOut()
    } else if ( resultObject.ErrorCode == AuthSystemError.ProtectedAccount ) { // 20076
      alert( $t('LOGOUT_BY_PROTECTED_ACCOUNT') )
      this.gotoSignOut()
    } else if ( resultObject.ErrorCode == AuthSystemError.Unknown ) { // 20000
      alert( $t('LOGOUT_BY_UNKNOWN') )
      this.gotoSignOut()
    } else if ( resultObject.ErrorCode == AuthSystemError.SessionDataNotExist ) { // 20013
      const err = new Error($t('LOGOUT_BY_OTHERS'))
      console.warn(err, 'ErrorCode:', resultObject.ErrorCode, 'ErrorMessage:', resultObject.ErrorMessage)
      this.gotoSignOut()
    } else if ( resultObject.ErrorCode == AuthSystemError.InvalidPassportKey ) { // 20015
      console.error('ErrorCode:', resultObject.ErrorCode, 'ErrorMessage:', resultObject.ErrorMessage)
      alert( $t('LOGOUT_BY_INVALID_SESSION') )
      this.gotoSignOut()
    } else if ( resultObject.ErrorCode == AuthSystemError.BlockedByAdmin ) { // 20010
      alert( $t('LOGOUT_BY_BLOCKED_BY_ADMIN') )
      this.gotoSignOut()
    } else if ( resultObject.ErrorCode == AuthSystemError.InvalidHWID ) { // 20050
      alert( $t('LOGOUT_BY_INVALID_HWID') )
      this.gotoSignOut()
    } else {
      const err = new Error($t('LOGOUT_BY_OTHERS'))
      console.error(err, 'ErrorCode:', resultObject.ErrorCode, 'ErrorMessage:', resultObject.ErrorMessage)
      alert(`${err.message}`)
      this.gotoSignOut()
    }
  }

  /**
   * 인증세션 상태를 확인합니다.
   * SSO 로그인 상태를 포함하여 Signed 결과가 반환됩니다.
   * @returns {Boolean}
   */
  isSignedIn() {
    return this.guest.isSignedIn() || this.hasSignedSSO() || this._hasValidWebToken
  }

  /**
   * SSO 로그인 인증 세션을 가지고 있는지 확인합니다.
   * 해당 값이 존재할 경우 SSO Signed 상태입니다
   */
  hasSignedSSO() {
    return !!this.client.getCookie(GLOBAL_VARIABLE.WEB_SSO_SESSION_KEY)
  }

  /**
   * 통합 로그인 페이지로 이동합니다.
   * @param {String} redirect_uri 로그인 성공 후 지정된 페이지로 이동합니다. 값이 없을 경우 로그인 요청한 페이지로 이동합니다.
   * @param {String} target 통합 로그인 페이지를 띄울 타겟을 지정합니다. [ _blank, _self(default), _parent, _top ]
   * @param {String} platform 로컬플랫폼을 지정합니다. [ krpc, jppc, arena_west, ... ]
   * @param {Object} options
   * @param {boolean} options.ial 콘솔을 통한 로그인 여부 (by IAL LINKING PAGE)
   * @param {string} options.krpc.memberAccessCode KRPC 로그인 진입처 구분 용도의 코드 값
   * @param {string} options.jppc.gm JPPC 로그인 진입처 구분 용도의 코드 값
   */
  gotoSignIn({ redirect_uri, target = '_self', platform, options = {} } = {}) {
    const platformType = platform?.type || this.platform.type
    if (platformType === 'krpc') {
      !options.krpc && (options.krpc = {})
      options.krpc.memberAccessCode = options.krpc.memberAccessCode || this.platform.memberAccessCode
    } else if (platformType === 'jppc') {
      !options.jppc && (options.jppc = {})
      options.jppc.gm = options.jppc.gm || this.platform.gm
    }

    const query = this.client.getQuery({ platform, redirect_uri, options })
    const url = `${ENV.INFACE_SIGNIN_URL}?${query}`
    window.open(url, target)
  }

  /**
   * 통합 로그아웃 페이지로 이동합니다.
   * @param {String} redirect_uri 로그아웃 성공 후 지정된 페이지로 이동합니다. 값이 없을 경우 로그아웃 요청한 페이지로 이동합니다.
   * @param {String} target 통합 로그인 페이지를 띄울 타겟을 지정합니다. [ _blank, _self(default), _parent, _top ]
   */
  async gotoSignOut({ redirect_uri, target = '_self' } = {}) {
    // 게스트 계정 로그아웃 성공 시 웹토큰 여부와 관계 없이 플로우 종료
    const { success } = await this.guest.processSignOut()
    if (success) {
      window.location.reload()
      return
    }

    // insign-gw 와 중복 호출이지만 사이드 이펙 방지를 위해 필요 (nexon.com 에서 로그아웃 선진행)
    await this.expireWebToken({ doClearSession: false })
    
    const query = this.client.getQuery({ redirect_uri })
    const url = `${ENV.INFACE_SIGNOUT_URL}?${query}`
    window.open(url, target)
  }

  /**
   * 통합 회원가입 페이지로 이동합니다.
   * @param {String} target 통합 로그인 페이지를 띄울 타겟을 지정합니다. [ _blank, _self(default), _parent, _top ]
   */
  gotoSignUp({ redirect_uri, target = '_self', platform } = {}) {
    const query = this.client.getQuery({ platform, redirect_uri })
    const url = `${ENV.INFACE_SIGNUP_URL}?${query}`
    window.open(url, target)
  }

  /**
   * 통합 계정관리 페이지로 이동합니다.
   * @param {String} target 통합 로그인 페이지를 띄울 타겟을 지정합니다. [ _blank, _self(default), _parent, _top ]
   */
  gotoAccount({ target = '_self' } = {}) {
    const query = this.client.getQuery()
    const url = `${ENV.INFACE_ACCOUNT_URL}?${query}`
    window.open(url, target)
  }

  /**
   * 약관 동의를 위해 통합 로그인 페이지로 이동합니다.
   * @param {String} redirect_uri 로그인 성공 후 지정된 페이지로 이동합니다. 값이 없을 경우 로그인 요청한 페이지로 이동합니다.
   * @param {String} target 통합 로그인 페이지를 띄울 타겟을 지정합니다. [ _blank, _self(default), _parent, _top ]
   * @param {String} platform 로컬플랫폼을 지정합니다. [ krpc, jppc, arena_west, ... ]
   * @param {Object} options
   * @param {boolean} options.ial 콘솔을 통한 로그인 여부 (by IAL LINKING PAGE)
   * @param {string} options.krpc.memberAccessCode KRPC 로그인 진입처 구분 용도의 코드 값
   * @param {string} options.jppc.gm JPPC 로그인 진입처 구분 용도의 코드 값
   */
  gotoLogin({ redirect_uri, target = '_self', platform, options = {} } = {}) {
    const platformType = platform?.type || this.platform.type
    if (platformType === 'krpc') {
      !options.krpc && (options.krpc = {})
      options.krpc.memberAccessCode = options.krpc.memberAccessCode || this.platform.memberAccessCode
    } else if (platformType === 'jppc') {
      !options.jppc && (options.jppc = {})
      options.jppc.gm = options.jppc.gm || this.platform.gm
    }

    const query = this.client.getQuery({ platform, redirect_uri, options })
    const url = `${ENV.INFACE_LOGIN_URL}?${query}`
    window.open(url, target)
  }

  /**
   * 추가 약관 동의를 위해 약관동의 페이지로 이동합니다.
   * @param {String} target 통합 로그인 페이지를 띄울 타겟을 지정합니다. [ _blank, _self(default), _parent, _top ]
   */
  gotoConsentTerms({ target = '_self', guid, ticket } = {}) {
    const query = this.client.getQuery()
    const url = `${ENV.INFACE_CONSENT_TERMS_URL}?${query}&guid=${guid}&ticket=${ticket}`
    window.open(url, target)
  }

  /**
   * cid 생성 함수
   */
  _generateCID() {
    return generateUUID()
  }

  /**
   * cid 조회 함수 for Toy Web SDK
   */
  getCID() {
    let cid = localStorage.getItem(GLOBAL_VARIABLE.CID_KEY)

    if (!cid) {
      cid = this._generateCID()
      localStorage.setItem(GLOBAL_VARIABLE.CID_KEY, cid)
    }

    return cid
  }

  /**
   * gid별 설정 조회
   * @param {String} gid
   * @returns {Boolean}
   */
  async getGidSetting({ gid = IF_GID } = {}) {
    try {
      if (!gid) {
        throw new InfaceAuthError({ code: 113011 })
      }
      
      const response = await Fetch(`${IMS_API_URL}/setting/${gid}`, {
        method: 'GET',
        mode: 'cors',
      })

      if (!response.ok) {
        throw response
      }

      const data = await response.json()
      return data

    } catch(e) {
      if (e instanceof InfaceAuthError) {
        throw e
      }

      const errorData = typeof e.json === "function" ? await e.json() : e
      const err = new InfaceAuthError({ code: 113201, response: e, data: errorData })
      throw err
    }
  }

  /**
   * [콘솔링킹 2차스펙(IAL)] 
   * Account Link 생성을 위한 IAL-Ticket 발급 요청
   * 
   * @param {Object} args
   * @param {string} args.console_type - 콘솔 타입 `(STEAM | XBOX | PSN)`
   * @param {string} args.console_token - 콘솔 인증 토큰
   * @param {string} args.gid - 게임 식별자
   * @param {string} args.language - QR/instruction 페이지의 설정 언어 (default: en) -> deprecated by args.locale
   * @param {string} args.locale - QR/instruction 페이지의 설정 언어 ex) zh_TW
   * @return {Object}
   */
  async getIalTicket({ console_type, console_token, gid, ...args }) {
    try {
      if (!console_type || !console_token || !gid) {
        throw new InfaceAuthError({ code: 113012, data: { console_type, console_token, gid } })
      }

      if (!this._platform) {
        throw new InfaceAuthError({ code: 113001, data: { platform: this._platform } })
      }

      const body = {
        gid,
        link_platform_type: console_type.toUpperCase(),
        link_platform_token: console_token,
        ...args
      }

      const response = await Fetch(`${ENV.INFACE_IMS_API_URL_V2}/issue/link-ticket`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...body })
      })

      if (!response.ok) {
        throw response
      }

      const data = await response.json()
      return { linkTicket: data.link_ticket, redirectUrl: data.redirect_url }

    } catch (e) {
      const errorData = typeof e.json === "function" ? await e.json() : e
      const err = e instanceof InfaceAuthError ? e : new InfaceAuthError({ code: 113202, response: e, data: errorData })

      throw err
    }
  }

  /**
   * TOY 특정 게임의 점검/차단 정보 조회
   * 
   * @param {String} gid
   * @return {Object} { success: Boolean, data: Object }
   */
  async getToyMaintenances({ gid = IF_GID } = {}) {
    try {
      if (!gid) {
        throw new InfaceAuthError({ code: 113011 })
      }

      const response = await Fetch(`${ENV.SDK_API_URL}/access-control/maintenances`, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'client-type': 'gs-web-sdk',
          gid,
        },
      })

      if (!response.ok) {
        throw response
      }

      const data = await response.json()

      // 성공은 code: 0
      if (data?.code === 0 && data?.result) {
        return { success: true, data: data.result }
      }

      return { success: false, data: {} }

    } catch(e) {
      const errorData = typeof e.json === "function" ? await e.json() : e
      const err = e instanceof InfaceAuthError ? e : new InfaceAuthError({ code: 113601, response: e, data: errorData })
      throw err
    }
  }

  /** @param {import('../services/inface.service').ToyWebSdkEvent} */
  async onInfaceModuleEventListener({ moduleName, eventData }) {
    if (moduleName === MODULE_NAME) return

    console.debug(`[web-auth] onInfaceEventListener - from [${moduleName}]\neventData:\n${JSON.stringify(eventData, null, 2)}`)
  }
}

export default InfaceAuth